import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { fetchApi } from './fetchApi';

interface Position {
  parent_id: number;
  parent_bezeichnung: string;
  anzahl_children: number;
  lagerwert: number;
}

interface LagerwertDaten {
  gesamtsumme: number;
  ohne_preis_count: number;
  positionen: Position[];
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function LagerwertGesamtTabelle() {
  const [daten, setDaten] = useState<LagerwertDaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    fetchApi('/api/view/lagerwert-gesamt')
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data: LagerwertDaten) => setDaten(data))
      .catch((err: any) => {
        setFehler(
          err?.message === '401'
            ? 'Session abgelaufen. Bitte neu einloggen.'
            : 'Daten konnten nicht geladen werden.'
        );
      })
      .finally(() => setLaedt(false));
  }, []);

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={2}>
        <Typography variant="h5">Lagerwert gesamt</Typography>
      </Box>

      {fehler && <Alert severity="error">{fehler}</Alert>}

      {laedt ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : daten && (
        <>
          {/* Gesamtsumme prominent */}
          <Paper
            elevation={3}
            sx={{
              p: 4,
              mb: 3,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: '#fff',
              borderRadius: 2,
            }}
          >
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>
              Gesamter Lagerwert (EK)
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
              {formatEuro(daten.gesamtsumme)}
            </Typography>
          </Paper>

          {/* Hinweis Artikel ohne Preis */}
          {daten.ohne_preis_count > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {daten.ohne_preis_count} Artikel mit Bestand haben keinen EK-Preis und wurden nicht berücksichtigt.
            </Alert>
          )}

          {/* Aufschlüsselung nach Parent */}
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Artikel</TableCell>
                  <TableCell align="center">Varianten</TableCell>
                  <TableCell align="right">Lagerwert (EK)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {daten.positionen.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ opacity: 0.7 }}>
                      Keine Daten vorhanden.
                    </TableCell>
                  </TableRow>
                ) : (
                  daten.positionen.map((pos) => (
                    <TableRow key={pos.parent_id}>
                      <TableCell>{pos.parent_bezeichnung}</TableCell>
                      <TableCell align="center">{pos.anzahl_children}</TableCell>
                      <TableCell align="right">{formatEuro(Number(pos.lagerwert))}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Container>
  );
}
